// cSpell:ignore betaalt Hendrik huur Huurder Ouder voogd
// Who receives the payment request of a settlement (or of a cost billed to a
// tenant right away). A lease can have several tenants and a parent or
// guardian, so the payer is chosen by role and by who pays the rent, never
// by the order the API lists the members in (that is the account id: whoever
// registered on Rentio first).

import { EContractMemberTypes } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { EPaymentOrderType } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatMoney } from "./moneyText.utils";

export type TPayerRole = "tenant" | "parent" | "other";

// Someone on the lease who can receive the payment request.
export interface IPayerCandidate {
  accountId: number;
  name: string;
  role: TPayerRole;
  // The payer of the lease's own rent payment order.
  paysRent: boolean;
}

interface IContractMemberLike {
  roles?: string[];
  account?: { id?: number } | null;
}

interface IRentOrderLike {
  id?: number;
  contractId?: string | null;
  type?: string;
  payerId?: number | null;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
}

export const RENT_ORDER_TYPES: string[] = [
  EPaymentOrderType.Rent,
  EPaymentOrderType.RentAndCosts,
];

const ROLE_LABEL_KEYS: Record<TPayerRole, string> = {
  tenant: "role.tenant",
  parent: "role.parent",
  other: "role.payer",
};

const timeOf = (date?: Date | string | null) =>
  date ? new Date(date).getTime() : 0;

// Of two rent orders of one lease, the one that says who pays the rent now:
// a running order before a completed one, then the latest start.
const isMoreCurrent = (params: {
  order: IRentOrderLike;
  current: IRentOrderLike;
}) => {
  const { order, current } = params;
  const isRunning = !order.completedAt;
  const isCurrentRunning = !current.completedAt;
  if (isRunning !== isCurrentRunning) return isRunning;
  const byStart = timeOf(order.startedAt) - timeOf(current.startedAt);
  if (byStart !== 0) return byStart > 0;
  return (order.id ?? 0) > (current.id ?? 0);
};

// contractId → the payer of that lease's rent (RENT or RENT_AND_COSTS order).
// Matched on the order's own contract, so a previous tenant's rent order on
// the same property never decides who pays for the next one.
export const getRentPayerByContract = (
  orders: IRentOrderLike[],
): Record<string, number> => {
  const latest: Record<string, IRentOrderLike> = {};
  for (const order of orders) {
    const { contractId, type, payerId } = order;
    if (
      !contractId ||
      payerId == null ||
      !RENT_ORDER_TYPES.includes(type ?? "")
    ) {
      continue;
    }
    const current = latest[contractId];
    if (!current || isMoreCurrent({ order, current })) {
      latest[contractId] = order;
    }
  }
  return Object.fromEntries(
    Object.entries(latest).map(([contractId, order]) => [
      contractId,
      Number(order.payerId),
    ]),
  );
};

// The tenants and the parents/guardians of a lease, tenants first. The
// payer of the lease's rent is always one of them, also when the lease lists
// them in another role.
export const getPayerCandidates = <
  TMember extends IContractMemberLike,
>(params: {
  members: TMember[];
  nameOf: (member: TMember) => string;
  rentPayerId?: number;
}): IPayerCandidate[] => {
  const { members, nameOf, rentPayerId } = params;
  const roleOf = (member: TMember): TPayerRole | undefined => {
    const roles = member.roles ?? [];
    if (roles.includes(EContractMemberTypes.Tenant)) return "tenant";
    if (roles.includes(EContractMemberTypes.Parent)) return "parent";
    if (rentPayerId != null && member.account?.id === rentPayerId) {
      return "other";
    }
    return undefined;
  };
  const roleOrder: TPayerRole[] = ["tenant", "parent", "other"];
  const candidates: IPayerCandidate[] = [];
  for (const role of roleOrder) {
    for (const member of members) {
      const accountId = member.account?.id;
      if (accountId == null || roleOf(member) !== role) continue;
      if (candidates.some(candidate => candidate.accountId === accountId)) {
        continue;
      }
      candidates.push({
        accountId,
        name: nameOf(member),
        role,
        paysRent: accountId === rentPayerId,
      });
    }
  }
  return candidates;
};

// The payer to use without asking: whoever pays the lease's rent, else the
// lease's only tenant or parent. Undefined when the broker has to choose
// (several tenants or a tenant and a parent, and no rent order).
export const getDefaultPayerId = (
  candidates: IPayerCandidate[],
): number | undefined => {
  const rentPayer = candidates.find(candidate => candidate.paysRent);
  if (rentPayer) return rentPayer.accountId;
  return candidates.length === 1 ? candidates[0].accountId : undefined;
};

// The payer in effect: the broker's own choice when it is still someone on
// the lease, otherwise the default. Undefined while it must be chosen.
export const getEffectivePayerId = (params: {
  candidates: IPayerCandidate[];
  chosenId?: number;
}): number | undefined => {
  const { candidates, chosenId } = params;
  if (
    chosenId != null &&
    candidates.some(candidate => candidate.accountId === chosenId)
  ) {
    return chosenId;
  }
  return getDefaultPayerId(candidates);
};

export const getPayerName = (params: {
  candidates?: IPayerCandidate[];
  accountId?: number;
}): string | undefined => {
  const { candidates, accountId } = params;
  if (accountId == null) return undefined;
  return candidates?.find(candidate => candidate.accountId === accountId)?.name;
};

// Who a lease's share of a cost or settlement bills right now: the broker's
// choice, else the default (see getEffectivePayerId). No payer while the
// broker has to choose.
export const getLeasePayer = (params: {
  candidates: IPayerCandidate[];
  chosenId?: number;
}): { payerId?: number; payerName?: string } => {
  const { candidates, chosenId } = params;
  const payerId = getEffectivePayerId({ candidates, chosenId });
  return {
    payerId,
    payerName: getPayerName({ candidates, accountId: payerId }),
  };
};

// Whether the broker sees who receives a lease's payment request (and can
// change it): a lease with several tenants, or a tenant and a parent.
export const hasPayerChoice = (candidates: IPayerCandidate[]): boolean =>
  candidates.length > 1;

// Everyone a cost on these leases can bill: their tenants, parents and rent
// payers. rentio-api-next refuses anyone else as a payer of the cost (see its
// splitPayers.utils), and only once the cost is saved, after the payment
// order billing them exists.
export const getBillablePayerIds = (
  leases: { payerCandidates?: IPayerCandidate[] }[],
): number[] =>
  Array.from(
    new Set(
      leases.flatMap(lease =>
        (lease.payerCandidates ?? []).map(candidate => candidate.accountId),
      ),
    ),
  );

// "Hendrik Huurder (Ouder/voogd) · betaalt de huur".
export const getPayerOptionLabel = (candidate: IPayerCandidate): string => {
  const label = `${candidate.name} (${getLocalizedText(
    ROLE_LABEL_KEYS[candidate.role],
  )})`;
  return candidate.paysRent
    ? `${label} · ${getLocalizedText("settlement.confirm.payer_pays_rent")}`
    : label;
};

// Who a payment request from a confirmed settlement goes to. The payer frozen
// on confirm, as long as they are still on the lease: when the lease's
// tenants changed since, nobody is preselected and the broker is told why.
// Older settlements froze no payer: the lease's current default then, or
// nobody when that is not clear. A deleted lease keeps the frozen payer.
export const getInvitePayer = (params: {
  frozenPayerId?: number;
  liveContract?: {
    tenantAccountId?: number;
    isPayerResolved?: boolean;
    payerCandidates?: IPayerCandidate[];
  };
}): { payerId?: number; hasPayersChanged: boolean } => {
  const { frozenPayerId, liveContract } = params;
  if (!liveContract) {
    return { payerId: frozenPayerId, hasPayersChanged: false };
  }
  const { payerCandidates, isPayerResolved, tenantAccountId } = liveContract;
  if (frozenPayerId != null) {
    const isStillOnLease =
      !payerCandidates ||
      payerCandidates.some(candidate => candidate.accountId === frozenPayerId);
    return isStillOnLease
      ? { payerId: frozenPayerId, hasPayersChanged: false }
      : { payerId: undefined, hasPayersChanged: true };
  }
  return {
    payerId: isPayerResolved ? tenantAccountId : undefined,
    hasPayersChanged: false,
  };
};

export interface IBilledShareLike {
  payerName?: string;
  // The lease's tenants (or parents), see mapContractToRental.
  tenantName: string;
  unitLabel?: string;
  // What the lease is billed, incl. VAT.
  amount?: number;
}

// Who a lease's payment request goes to, by name: its payer (the parent of
// a student, say), and only while no payer is known the lease's tenants.
export const getRecipientName = (share: IBilledShareLike): string =>
  share.payerName || share.tenantName || getLocalizedText("costs.field.tenant");

type TRecipientDetail = (share: IBilledShareLike) => string | undefined;

// What tells two payment requests to the same person apart: the lease's
// tenants (a parent who pays for two students), its unit, and the amount
// (two leases of one tenant in the same unit, one after the other).
const leaseTenantsOf: TRecipientDetail = share =>
  share.tenantName && share.tenantName !== getRecipientName(share)
    ? share.tenantName
    : undefined;
const unitOf: TRecipientDetail = share => share.unitLabel || undefined;
// "€ 95,40" as the rest of the feature prints it (see fmt), the "€" kept
// with its number.
const amountOf: TRecipientDetail = share =>
  share.amount == null ? undefined : formatMoney(share.amount);

// The details a name gets, fewest first: the first set that tells every
// request to that person apart is used, else all of them.
const RECIPIENT_DETAIL_SETS: TRecipientDetail[][] = [
  [leaseTenantsOf],
  [unitOf],
  [amountOf],
  [leaseTenantsOf, unitOf],
  [leaseTenantsOf, amountOf],
  [unitOf, amountOf],
  [leaseTenantsOf, unitOf, amountOf],
];
const ALL_RECIPIENT_DETAILS =
  RECIPIENT_DETAIL_SETS[RECIPIENT_DETAIL_SETS.length - 1];

// "Hans Huurder (Hendrik Huurder)", or just "Hans Huurder" when the share
// has none of these details.
const getRecipientLabel = (params: {
  share: IBilledShareLike;
  details: TRecipientDetail[];
}): string => {
  const { share, details } = params;
  const name = getRecipientName(share);
  const parts = details
    .map(detail => detail(share))
    .filter((part): part is string => !!part);
  return parts.length > 0 ? `${name} (${parts.join(" · ")})` : name;
};

// Whether these details give each of the shares a label of its own.
const isEachToldApart = (params: {
  shares: IBilledShareLike[];
  details: TRecipientDetail[];
}): boolean => {
  const { shares, details } = params;
  const labels = shares.map(share => getRecipientLabel({ share, details }));
  return new Set(labels).size === shares.length;
};

// "Hans Huurder (Hendrik Huurder), Hugo Huurder": who `shares` bill, in a
// message that names them along with `among` (every share the broker sees
// with them: the billed and the missing ones of a bulk submission that failed
// halfway, or the ones still in the form below). A name that comes back for
// another lease anywhere in `among` gets, in every list of the message, the
// details that tell its leases apart: "billed" and "missing" no longer name
// two of them the same.
export const joinRecipientNamesAmong = (params: {
  shares: IBilledShareLike[];
  among: IBilledShareLike[];
}): string => {
  const { shares, among } = params;
  return shares
    .map(share => {
      const name = getRecipientName(share);
      const pool = among.includes(share) ? among : [...among, share];
      const namesakes = pool.filter(other => getRecipientName(other) === name);
      if (namesakes.length < 2) return name;
      const details =
        RECIPIENT_DETAIL_SETS.find(set =>
          isEachToldApart({ shares: namesakes, details: set }),
        ) ?? ALL_RECIPIENT_DETAILS;
      return getRecipientLabel({ share, details });
    })
    .join(", ");
};

// The same for a message that names only these shares.
export const joinRecipientNames = (shares: IBilledShareLike[]): string =>
  joinRecipientNamesAmong({ shares, among: shares });
